import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Header from "../core/Header";
import Footer from "../core/Footer";
import PageHero from "../components/PageHero";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
// import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import emailjs from "@emailjs/browser";
import { useLocation } from "react-router-dom";
import { useTranslation } from "../utils/i18n";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const CONTACT_ITEMS = [
  {
    key: "phone",
    icon: <PhoneOutlinedIcon sx={{ fontSize: 22, color: "#FF6A00" }} />,
    value: "+46 (0) 523 166 10",
  },
  {
    key: "email",
    icon: <MailOutlineIcon sx={{ fontSize: 22, color: "#FF6A00" }} />,
    value: "info@radiosmd.se",
    href: "mailto:info@radiosmd.se",
  },
  {
    key: "address",
    icon: <LocationOnOutlinedIcon sx={{ fontSize: 22, color: "#FF6A00" }} />,
    value: "Södra Hamngatan 16, 453 33 Lysekil",
    href: null,
  },
];

const INITIAL = { name: "", email: "", company: "", subject: "", message: "" };

export default function Contact() {
  const { t, translations } = useTranslation();
  const cp = translations.contactPage;
  const location = useLocation();
  const [fields, setFields] = React.useState({
    ...INITIAL,
    subject: location.state?.subject ?? "",
  });
  const [status, setStatus] = React.useState("idle"); // idle | sending | sent | error

  const handleChange = (e) => {
    setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          name: fields.name,
          email: fields.email,
          company: fields.company,
          subject: fields.subject,
          message: fields.message,
        },
        PUBLIC_KEY,
      );
      setStatus("sent");
      setFields(INITIAL);
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("error");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#F6F7F9",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      <PageHero
        overline={cp.overline}
        title={cp.title}
        subtitle={cp.subtitle}
      />

      <Box
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: 1440,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 5 },
          pt: { xs: 5, md: 8 },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 420px" },
            gap: { xs: 4, md: 6 },
            alignItems: "start",
          }}
        >
          {/* Contact form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              bgcolor: "#fff",
              borderRadius: 3,
              border: "1px solid #E3E6EB",
              p: { xs: 3, md: 4.5 },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.03em",
                color: "#0B0F13",
                mb: 0.75,
              }}
            >
              {t("contactPage.formTitle")}
            </Typography>
            <Typography sx={{ color: "#475467", fontSize: "0.92rem", mb: 3.5 }}>
              {t("contactPage.responseTime")}
            </Typography>

            {status === "sent" ? (
              <Box
                sx={{
                  bgcolor: "rgba(255,106,0,0.07)",
                  border: "1px solid rgba(255,106,0,0.25)",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontWeight: 700, color: "#FF6A00", mb: 0.5 }}>
                  {t("contactPage.successTitle")}
                </Typography>
                <Typography sx={{ color: "#475467", fontSize: "0.92rem" }}>
                  {t("contactPage.successBody")}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2.5,
                  }}
                >
                  <TextField
                    label={cp.fields.name}
                    name="name"
                    value={fields.name}
                    onChange={handleChange}
                    required
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                  <TextField
                    label={cp.fields.email}
                    name="email"
                    type="email"
                    value={fields.email}
                    onChange={handleChange}
                    required
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                </Box>
                <TextField
                  label={cp.fields.company}
                  name="company"
                  value={fields.company}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
                <TextField
                  label={cp.fields.subject}
                  name="subject"
                  value={fields.subject}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
                <TextField
                  label={cp.fields.message}
                  name="message"
                  value={fields.message}
                  onChange={handleChange}
                  required
                  fullWidth
                  multiline
                  rows={5}
                  sx={inputSx}
                />

                {status === "error" && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "0.88rem" }}>
                    {cp.errorMsg}
                  </Typography>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={status === "sending"}
                  startIcon={
                    status === "sending" ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : null
                  }
                  sx={{
                    alignSelf: "flex-start",
                    bgcolor: "#FF6A00",
                    color: "#fff",
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.2,
                    px: 3.5,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#E05500", boxShadow: "none" },
                    "&.Mui-disabled": { bgcolor: "#FF6A00", opacity: 0.6 },
                  }}
                >
                  {status === "sending" ? cp.sending : cp.send}
                </Button>
              </Box>
            )}
          </Box>

          {/* Contact info */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {CONTACT_ITEMS.map((item) => (
              <Box
                key={item.key}
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #E3E6EB",
                  borderRadius: 3,
                  p: { xs: 2.5, md: 3 },
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    bgcolor: "rgba(255,106,0,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: "#9AA4B2",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      mb: 0.25,
                    }}
                  >
                    {cp.info[item.key]}
                  </Typography>
                  {item.href ? (
                    <Typography
                      component="a"
                      href={item.href}
                      sx={{
                        color: "#0B0F13",
                        fontWeight: 600,
                        fontSize: "0.97rem",
                        textDecoration: "none",
                        "&:hover": { color: "#FF6A00" },
                      }}
                    >
                      {item.value}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        color: "#0B0F13",
                        fontWeight: 600,
                        fontSize: "0.97rem",
                      }}
                    >
                      {item.value}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    bgcolor: "#F6F7F9",
    "& fieldset": { borderColor: "#E3E6EB" },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: "#FF6A00" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#FF6A00" },
};
